import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Link2, Users, Megaphone, Bot, Headphones, BarChart3, ArrowRight, ChevronRight, ChevronDown, CheckCircle2,
  Database, Filter, Send, MessageSquare, Target, UserCheck, ShoppingBag, Repeat,
} from "lucide-react";
import { SectionHeading } from "@/components/Sections";
import { MOCKUPS } from "@/components/ProductMockups";

/* ======================================================= How GrabNChat works */
const HOW_STEPS = [
  { icon: Link2, title: "Connect WhatsApp", desc: "Connect your business with the official WhatsApp Business API through Meta's Embedded Signup.", to: "/products/whatsapp-business-api", link: "WhatsApp Business API",
    ui: <span className="inline-flex items-center gap-1 rounded-full bg-[#25D366]/10 px-2 py-0.5 text-[11px] font-semibold text-[#128C4A]"><CheckCircle2 className="w-3 h-3" /> +91 93xxx connected</span> },
  { icon: Users, title: "Import your customers", desc: "Organise contacts, customer information, tags and attributes in one place.", to: "/products/whatsapp-crm", link: "WhatsApp CRM",
    ui: <span className="flex gap-1">{["vip", "delhi", "new-lead"].map((t) => <span key={t} className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700">{t}</span>)}</span> },
  { icon: Megaphone, title: "Create campaigns", desc: "Create personalised WhatsApp broadcasts, promotions and customer notifications.", to: "/products/bulk-whatsapp-marketing", link: "Bulk WhatsApp marketing",
    ui: <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700">diwali_offer · Scheduled Sat 10 AM</span> },
  { icon: Bot, title: "Automate conversations", desc: "Build no-code chatbot flows, automated replies and follow-up journeys.", to: "/products/whatsapp-chatbot", link: "WhatsApp chatbot",
    ui: <span className="rounded-md bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-700">“price” → Bot → Lead saved</span> },
  { icon: Headphones, title: "Manage your team", desc: "Use the shared inbox to handle customer conversations across multiple agents.", to: "/products/shared-team-inbox", link: "Shared team inbox",
    ui: <span className="flex -space-x-1.5">{["RK", "AS", "NM"].map((a, i) => <span key={a} className={`w-6 h-6 rounded-full border-2 border-white grid place-items-center text-[9px] font-bold text-white ${["bg-slate-800", "bg-[#25D366]", "bg-violet-500"][i]}`}>{a}</span>)}</span> },
  { icon: BarChart3, title: "Track results", desc: "Monitor delivery, read, reply, campaign and conversion performance.", to: "/products/whatsapp-broadcast", link: "Campaign analytics",
    ui: <span className="flex items-end gap-0.5 h-5" aria-hidden>{[40, 65, 50, 80, 70, 95].map((h, i) => <span key={i} className="w-2 rounded-sm bg-[#25D366]/80" style={{ height: `${h}%` }} />)}</span> },
];

export function HowGrabNChatWorks() {
  return (
    <section id="how-it-works" className="py-20 md:py-28 scroll-mt-20" data-testid="how-it-works">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading center eyebrow="How it works" title="How GrabNChat Works"
          sub="Connect → Customers → Campaign → Automation → Team → Analytics. Six steps from sign-up to measurable results." />
        <ol className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {HOW_STEPS.map((s, i) => (
            <li key={s.title} className="relative flex flex-col bg-white border border-slate-200 rounded-2xl p-6 min-w-0">
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-xl grid place-items-center bg-[#25D366] text-white"><s.icon className="w-5 h-5" aria-hidden /></span>
                <span className="text-xs font-bold tracking-widest text-[#25D366]">STEP {i + 1}</span>
              </div>
              <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">{s.title}</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed flex-1">{s.desc}</p>
              <div className="mt-4 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-2.5">{s.ui}</div>
              <Link to={s.to} className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#25D366]">{s.link} <ArrowRight className="w-4 h-4" /></Link>
              {(i + 1) % 3 !== 0 && (
                <ChevronRight className="hidden lg:block absolute -right-[18px] top-1/2 -translate-y-1/2 w-6 h-6 text-[#25D366] z-10" aria-hidden />
              )}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ================================================ Customer journey diagram */
const JOURNEY = [
  { icon: Database, title: "Customer data", desc: "Contacts from chats, ads, forms, your store or an import.", to: "/products/whatsapp-crm", link: "WhatsApp CRM" },
  { icon: Filter, title: "Audience segmentation", desc: "Group customers by tags and attributes like city or plan.", to: "/products/whatsapp-crm", link: "Tags & attributes" },
  { icon: Send, title: "WhatsApp campaign", desc: "A personalised, approved template reaches the right people.", to: "/products/bulk-whatsapp-marketing", link: "WhatsApp campaigns" },
  { icon: MessageSquare, title: "Automated response", desc: "The chatbot replies instantly to every answer or question.", to: "/products/whatsapp-chatbot", link: "Chatbot" },
  { icon: Target, title: "Lead qualification", desc: "The bot asks the right questions and saves the lead.", to: "/products/lead-management", link: "Lead management" },
  { icon: UserCheck, title: "Sales follow-up", desc: "An agent picks up the chat in the shared inbox with full context.", to: "/products/shared-team-inbox", link: "Team inbox" },
  { icon: ShoppingBag, title: "Customer conversion", desc: "Share products, confirm the order and send updates.", to: "/products/whatsapp-catalog-ecommerce", link: "WhatsApp commerce" },
  { icon: Repeat, title: "Re-engagement", desc: "Drip campaigns bring customers back for the next purchase.", to: "/products/drip-campaigns", link: "Drip campaigns" },
];

export function CustomerJourney() {
  return (
    <section className="py-20 md:py-28 bg-[#0F172A] relative overflow-hidden" data-testid="customer-journey">
      <div className="absolute inset-0 bg-dots opacity-20" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading dark center eyebrow="WhatsApp marketing automation" title="Turn WhatsApp Conversations Into Customer Journeys"
          sub="GrabNChat connects every step of the funnel — from the first message to the repeat purchase — on one WhatsApp number." />
        <ol className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-8">
          {JOURNEY.map((s, i) => (
            <li key={s.title} className="relative min-w-0">
              <div className="h-full rounded-2xl border border-white/10 bg-white/[0.04] p-5">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl grid place-items-center bg-[#25D366]/15 text-[#25D366]"><s.icon className="w-5 h-5" aria-hidden /></span>
                  <span className="text-[11px] font-bold tracking-widest text-slate-500">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="mt-3 font-display text-base font-semibold text-white">{s.title}</h3>
                <p className="mt-1 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                <Link to={s.to} className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#25D366] hover:underline">{s.link} <ArrowRight className="w-3.5 h-3.5" /></Link>
              </div>
              {i < JOURNEY.length - 1 && (
                <>
                  {(i + 1) % 4 !== 0 && <ChevronRight className="hidden lg:block absolute -right-5 top-1/2 -translate-y-1/2 w-5 h-5 text-[#25D366]/70" aria-hidden />}
                  <ChevronDown className="sm:hidden mx-auto mt-2 w-5 h-5 text-[#25D366]/70" aria-hidden />
                </>
              )}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* =============================================== See GrabNChat in action */
const DEMO_TABS = [
  { key: "marketing", label: "Marketing", mockup: "campaign", to: "/products/bulk-whatsapp-marketing", link: "Explore WhatsApp marketing",
    title: "Personalised WhatsApp campaigns",
    desc: "Send approved templates to the right segment, personalised with each customer's details, and see who received, read and replied.",
    benefits: ["Target by tags and attributes", "Images, videos, documents and buttons", "Schedule and track in real time"],
    flow: ["Audience", "Template", "Personalise", "Schedule", "Results"] },
  { key: "chatbot", label: "Chatbot", mockup: "chatbot", to: "/products/whatsapp-chatbot", link: "Explore the chatbot builder",
    title: "No-code WhatsApp chatbot",
    desc: "Answer FAQs, qualify leads and book appointments 24/7 with drag-and-drop flows, then hand over to a human when needed.",
    benefits: ["Buttons, lists and questions", "Saves leads automatically", "Handoff to the shared inbox"],
    flow: ["Message", "Bot reply", "Question", "Lead saved", "Agent"] },
  { key: "crm", label: "CRM", mockup: "crm", to: "/products/whatsapp-crm", link: "Explore the WhatsApp CRM",
    title: "A WhatsApp CRM for every contact",
    desc: "Every contact's tags, custom attributes, lead stage and full conversation history in one profile.",
    benefits: ["Tags and custom attributes", "Lead stages and owners", "Complete chat history"],
    flow: ["Lead", "Profile", "Conversation", "Follow-up", "Customer"] },
  { key: "inbox", label: "Team Inbox", mockup: "inbox", to: "/products/shared-team-inbox", link: "Explore the team inbox",
    title: "One inbox for your whole team",
    desc: "Your sales and support agents reply from the same WhatsApp number, with each conversation assigned to an owner.",
    benefits: ["WhatsApp, Instagram & Messenger", "Assignment and tags", "No chat missed or answered twice"],
    flow: ["New chat", "Assigned", "Reply", "Resolved"] },
  { key: "automation", label: "Automation", mockup: "automation", to: "/products/whatsapp-automation", link: "Explore WhatsApp automation",
    title: "Follow-ups that run themselves",
    desc: "Keyword replies, chatbot flows and drip sequences keep every lead warm without manual reminders.",
    benefits: ["Keyword auto-replies", "Timed drip campaigns", "Stops when the customer converts"],
    flow: ["Trigger", "Message", "Delay", "Follow-up", "Converted"] },
  { key: "analytics", label: "Analytics", mockup: "analytics", to: "/products/whatsapp-broadcast", link: "Explore campaign reporting",
    title: "Know what's working",
    desc: "Track messages sent and received, delivery and read rates, replies and your best-performing templates.",
    benefits: ["Daily message volume", "Delivery status breakdown", "Top templates"],
    flow: ["Sent", "Delivered", "Read", "Replied"] },
];

export function ProductDemoTabs() {
  const [active, setActive] = useState(DEMO_TABS[0].key);
  return (
    <section className="py-20 md:py-28" data-testid="product-demo">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading center eyebrow="Product tour" title="See GrabNChat in Action"
          sub="Pick a feature to see the screen, what it does and how it fits into your workflow." />
        <div role="tablist" aria-label="GrabNChat features" className="mt-10 flex flex-wrap justify-center gap-2">
          {DEMO_TABS.map((t) => (
            <button key={t.key} type="button" role="tab" id={`demo-tab-${t.key}`} aria-controls={`demo-panel-${t.key}`} aria-selected={active === t.key}
              onClick={() => setActive(t.key)}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${active === t.key ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-slate-700 border-slate-200 hover:border-[#25D366]"}`}>
              {t.label}
            </button>
          ))}
        </div>
        {/* All panels stay in the DOM (inactive ones hidden) so their text is crawlable. */}
        {DEMO_TABS.map((t) => {
          const M = MOCKUPS[t.mockup];
          return (
            <div key={t.key} role="tabpanel" id={`demo-panel-${t.key}`} aria-labelledby={`demo-tab-${t.key}`} hidden={active !== t.key}
              className={`mt-10 lg:grid-cols-2 gap-10 items-center ${active === t.key ? "grid" : "hidden"}`}>
              <div className="min-w-0 order-2 lg:order-1">
                <h3 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">{t.title}</h3>
                <p className="mt-3 text-slate-600 leading-relaxed">{t.desc}</p>
                <ul className="mt-5 space-y-2.5">
                  {t.benefits.map((b) => <li key={b} className="flex items-start gap-2 text-slate-800"><CheckCircle2 className="w-5 h-5 text-[#25D366] mt-0.5 shrink-0" /> {b}</li>)}
                </ul>
                <ol className="mt-6 flex flex-wrap items-center gap-1.5 text-xs font-semibold text-slate-600" aria-label="Workflow">
                  {t.flow.map((f, i) => (
                    <li key={f} className="flex items-center gap-1.5">
                      <span className="rounded-full bg-[#25D366]/10 text-[#128C4A] px-2.5 py-1">{f}</span>
                      {i < t.flow.length - 1 && <ChevronRight className="w-3.5 h-3.5 text-slate-400" aria-hidden />}
                    </li>
                  ))}
                </ol>
                <Link to={t.to} className="mt-7 inline-flex items-center gap-1.5 font-semibold text-[#25D366]">{t.link} <ArrowRight className="w-4 h-4" /></Link>
              </div>
              <div className="min-w-0 order-1 lg:order-2"><M /></div>
            </div>
          );
        })}
        <p className="mt-6 text-center text-xs text-slate-400">Previews use sample data.</p>
      </div>
    </section>
  );
}
